import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col, Typography, Space, Divider } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, HomeOutlined, SaveOutlined, CloseOutlined, IdcardOutlined, TeamOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../variables/api/axiosInstance';
import { codeOrganismeCourant, peutModifierRetenueFiscale } from '../../services/rightsService';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const EditIntermediaire = ({ show, handleClose, intermediaire, agence, onUpdated }) => {
    // Taux de retenue : administrateur du siège de la compagnie de l'intermédiaire seulement (le serveur
    // conserve le taux actuel pour les autres).
    const peutModifierTaux = peutModifierRetenueFiscale()
        && (!intermediaire?.organisme?.code || intermediaire.organisme.code === codeOrganismeCourant());
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [mailExists, setMailExists] = useState(false);
    const typePersonne = Form.useWatch('typePersonne', form);
    const isMorale = typePersonne === 'MORALE';

    // Thème de couleurs
    const colors = {
        primary: '#242166',
        secondary: '#1890ff',
        success: '#52c41a',
        error: '#ff4d4f',
        gradient: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
        lightBg: '#f5f7fa'
    };

    const checkIfEmailExists = async (email) => {
        try {
            const res = await axiosInstance.get(`api/utilisateur/check/${email}`);
            if (res.data && email !== intermediaire?.personne?.email) {
                toast.warning("Il existe déjà un utilisateur avec cette adresse mail dans le système!");
            }
            setMailExists(res.data && email !== intermediaire?.personne?.email);
            return res.data && email !== intermediaire?.personne?.email;
        } catch (err) {
            console.log(err);
            return false;
        }
    };

    const handleEmailChange = (e) => {
        const email = e.target.value;
        if (email) {
            const fullEmail = agence?.organisme?.domaine ? `${email}@${agence?.organisme?.domaine}` : email;
            checkIfEmailExists(fullEmail);
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);

        try {
            const morale = values.typePersonne === "MORALE";

            // Email facultatif : suffixe + vérification uniquement s'il est renseigné
            let email = values.email || "";
            if (email && agence?.organisme?.domaine) {
                email += "@" + agence?.organisme?.domaine;
            }
            if (email) {
                const emailExists = await checkIfEmailExists(email);
                if (emailExists) {
                    setLoading(false);
                    return;
                }
            }

            const updatedIntermediaire = {
                ...intermediaire,
                personne: {
                    ...intermediaire.personne,
                    // Morale : la raison sociale sert de nom ; pas de prénom/genre
                    nom: morale ? values.raisonSociale : values.nom,
                    prenom: morale ? "" : values.prenom,
                    genre: morale ? null : values.genre,
                    typePersonne: values.typePersonne,
                    numeroRegistreCommerce: morale ? values.numeroRegistreCommerce : null,
                    nif: morale ? values.nif : null,
                    email: email || null,
                    contact: values.telephone ? [values.telephone] : [],
                    adresse: values.adresseResidence,
                },
                type: values.type,
                agence: agence,
                codeIntermediaire: values.codeIntermediaire,
                tauxRetenueFiscale: values.tauxRetenueFiscale
            };

            const res = await axiosInstance.put(`/api/intermediaire`, JSON.stringify(updatedIntermediaire));
            toast.success("Intermédiaire mis à jour avec succès");
            // Rester sur la liste : on remonte la version mise à jour (nouveau codeId dû à
            // l'historisation) + l'ancien codeId, pour que le parent réactualise UNIQUEMENT la ligne
            // concernée, sans recharger toute la page. Repli sur updatedIntermediaire si res.data vide.
            onUpdated?.(res.data || updatedIntermediaire, intermediaire?.codeId);
            handleClose();
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors de la mise à jour de l'intermédiaire");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (intermediaire) {
            const tp = intermediaire?.personne?.typePersonne || "PHYSIQUE";
            form.setFieldsValue({
                typePersonne: tp,
                nom: intermediaire?.personne?.nom,
                prenom: intermediaire?.personne?.prenom,
                genre: intermediaire?.personne?.genre,
                // Morale : la raison sociale est stockée dans nom
                raisonSociale: intermediaire?.personne?.nom,
                numeroRegistreCommerce: intermediaire?.personne?.numeroRegistreCommerce,
                nif: intermediaire?.personne?.nif,
                adresseResidence: intermediaire?.personne?.adresse,
                telephone: intermediaire?.personne?.contact && intermediaire?.personne?.contact[0],
                email: agence?.organisme?.domaine
                    ? intermediaire?.personne?.email?.split("@")[0]
                    : intermediaire?.personne?.email,
                type: intermediaire?.type,
                codeIntermediaire: intermediaire?.codeIntermediaire,
                // ?? et non || : 0 (exonéré) est un vrai taux.
                tauxRetenueFiscale: intermediaire?.tauxRetenueFiscale ?? 20
            });
        }
    }, [intermediaire, agence, form]);

    const phoneValidator = (_, value) => {
        const phonePattern = /^(22|90|91|92|93|96|98|99|70|71|79)\d{6}$/;
        if (!value) {
            // Téléphone facultatif
            return Promise.resolve();
        }
        if (!phonePattern.test(value)) {
            return Promise.reject(new Error('Numéro de téléphone invalide'));
        }
        return Promise.resolve();
    };

    return (
        <Modal
            open={show}
            onCancel={handleClose}
            footer={null}
            width={900}
            centered
            style={{ top: 20 }}
            closeIcon={<CloseOutlined style={{ color: colors.primary, fontSize: '18px' }} />}
        >
            <div style={{ padding: '8px 0' }}>
                {/* Header */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '32px',
                    paddingBottom: '24px',
                    borderBottom: `3px solid ${colors.primary}`
                }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: colors.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                        boxShadow: '0 4px 12px rgba(36, 33, 102, 0.3)'
                    }}>
                        <UserOutlined style={{ fontSize: '32px', color: 'white' }} />
                    </div>
                    <Title level={3} style={{
                        margin: '0 0 8px 0',
                        color: colors.primary
                    }}>
                        Modifier l'intermédiaire
                    </Title>
                    <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
                        Modifier les informations de l'intermédiaire de l'agence
                    </Text>
                </div>

                {/* Form */}
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    requiredMark="optional"
                >
                    <Row gutter={16}>
                        {/* Nature de l'intermédiaire */}
                        <Col xs={24}>
                            <Form.Item
                                name="typePersonne"
                                label={<Text strong style={{ color: colors.primary }}>Nature de l'intermédiaire</Text>}
                            >
                                <Select size="large" style={{ borderRadius: '8px' }}>
                                    <Option value="PHYSIQUE">Personne physique</Option>
                                    <Option value="MORALE">Personne morale</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        {isMorale ? (
                            <>
                                {/* Raison sociale */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="raisonSociale"
                                        label={<Text strong style={{ color: colors.primary }}>Raison sociale</Text>}
                                        rules={[{ required: true, message: 'Raison sociale requise' }]}
                                    >
                                        <Input prefix={<UserOutlined style={{ color: colors.secondary }} />}
                                            placeholder="Raison sociale" size="large" style={{ borderRadius: '8px' }} />
                                    </Form.Item>
                                </Col>
                                {/* RCCM */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="numeroRegistreCommerce"
                                        label={<Text strong style={{ color: colors.primary }}>Registre de commerce (RCCM)</Text>}
                                    >
                                        <Input placeholder="N° registre de commerce" size="large" style={{ borderRadius: '8px' }} />
                                    </Form.Item>
                                </Col>
                                {/* NIF */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="nif"
                                        label={<Text strong style={{ color: colors.primary }}>NIF</Text>}
                                    >
                                        <Input placeholder="Numéro d'identification fiscale" size="large" style={{ borderRadius: '8px' }} />
                                    </Form.Item>
                                </Col>
                            </>
                        ) : (
                            <>
                                {/* Nom */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="nom"
                                        label={<Text strong style={{ color: colors.primary }}>Nom</Text>}
                                        rules={[
                                            { required: true, message: 'Nom requis' },
                                            { min: 2, message: 'Le nom doit avoir au moins 2 caractères' }
                                        ]}
                                    >
                                        <Input
                                            prefix={<UserOutlined style={{ color: colors.secondary }} />}
                                            placeholder="Entrez le nom"
                                            size="large"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Item>
                                </Col>

                                {/* Prénom */}
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="prenom"
                                        label={<Text strong style={{ color: colors.primary }}>Prénom(s)</Text>}
                                        rules={[
                                            { required: true, message: 'Prénom requis' },
                                            { min: 2, message: 'Le prénom doit avoir au moins 2 caractères' }
                                        ]}
                                    >
                                        <Input
                                            prefix={<UserOutlined style={{ color: colors.secondary }} />}
                                            placeholder="Entrez le prénom"
                                            size="large"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </>
                        )}

                        {/* Code Intermédiaire */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="codeIntermediaire"
                                label={<Text strong style={{ color: colors.primary }}>Code de l'intermédiaire</Text>}
                                rules={[{ required: true, message: 'Code intermédiaire requis' }]}
                            >
                                <Input
                                    prefix={<IdcardOutlined style={{ color: colors.secondary }} />}
                                    placeholder="Entrez le code intermédiaire"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Genre (personne physique uniquement) */}
                        {!isMorale && (
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="genre"
                                    label={<Text strong style={{ color: colors.primary }}>Genre</Text>}
                                    rules={[{ required: true, message: 'Genre requis' }]}
                                >
                                    <Select
                                        placeholder="Sélectionnez le genre"
                                        size="large"
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <Option value="MASCULIN">Masculin</Option>
                                        <Option value="FEMININ">Féminin</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Type d'intermédiaire */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="type"
                                label={<Text strong style={{ color: colors.primary }}>Type d'intermédiaire</Text>}
                                rules={[{ required: true, message: 'Veuillez sélectionner le type d\'intermédiaire' }]}
                            >
                                <Select
                                    placeholder="Sélectionnez le type d'intermédiaire"
                                    size="large"
                                    suffixIcon={<TeamOutlined style={{ color: colors.secondary }} />}
                                    style={{ borderRadius: '8px' }}
                                >
                                    <Option value="AGENT_GENERAL">Agent général</Option>
                                    <Option value="APPORTEUR_REGULIER">Apporteur régulier</Option>
                                    <Option value="APPORTEUR_OCCASIONNEL">Apporteur occasionnel</Option>
                                    <Option value="COURTIER">Courtier</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Taux de retenue fiscale */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="tauxRetenueFiscale"
                                label={<Text strong style={{ color: colors.primary }}>Taux de retenue fiscale</Text>}
                                extra={peutModifierTaux ? null : "Modifiable par l'administrateur du siège uniquement"}
                            >
                                <Select size="large" style={{ borderRadius: '8px' }} placeholder="Retenue fiscale" disabled={!peutModifierTaux}>
                                    <Option value={0}>Exonéré (0 %)</Option>
                                    <Option value={3}>3 %</Option>
                                    <Option value={5}>5 %</Option>
                                    <Option value={20}>20 %</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Email */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="email"
                                label={<Text strong style={{ color: colors.primary }}>Adresse mail <Text type="secondary" style={{ fontWeight: 400 }}>(facultatif)</Text></Text>}
                                rules={[
                                    { type: 'email', message: 'L\'adresse email n\'est pas valide' }
                                ]}
                            >
                                <Input
                                    prefix={<MailOutlined style={{ color: colors.secondary }} />}
                                    suffix={agence?.organisme?.domaine && (
                                        <Text style={{ color: '#8c8c8c' }}>@{agence?.organisme?.domaine}</Text>
                                    )}
                                    placeholder="Veuillez renseigner l'adresse mail"
                                    size="large"
                                    onChange={handleEmailChange}
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Téléphone */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="telephone"
                                label={<Text strong style={{ color: colors.primary }}>Numéro de téléphone <Text type="secondary" style={{ fontWeight: 400 }}>(facultatif)</Text></Text>}
                                rules={[{ validator: phoneValidator }]}
                            >
                                <Input
                                    prefix={<PhoneOutlined style={{ color: colors.secondary }} />}
                                    addonBefore={<Text strong>TG (+228)</Text>}
                                    placeholder="00 00 00 00"
                                    size="large"
                                    maxLength={8}
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Adresse */}
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="adresseResidence"
                                label={<Text strong style={{ color: colors.primary }}>Adresse de résidence</Text>}
                            >
                                <TextArea
                                    placeholder="L'adresse de résidence de l'intermédiaire"
                                    maxLength={255}
                                    rows={4}
                                    showCount
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '24px 0', borderColor: '#f0f0f0' }} />

                    {/* Footer Buttons */}
                    <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
                        <Space size="middle">
                            <Button
                                size="large"
                                onClick={handleClose}
                                icon={<CloseOutlined />}
                                style={{
                                    borderRadius: '8px',
                                    minWidth: '120px',
                                    height: '44px'
                                }}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={loading}
                                icon={<SaveOutlined />}
                                style={{
                                    background: colors.gradient,
                                    border: 'none',
                                    borderRadius: '8px',
                                    minWidth: '140px',
                                    height: '44px',
                                    boxShadow: '0 4px 12px rgba(36, 33, 102, 0.3)'
                                }}
                            >
                                {loading ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default EditIntermediaire;